import {
    type BatchedMesh,
    BufferGeometry,
    type Camera,
    Color,
    Matrix4,
    Object3D,
    Quaternion,
    Vector3,
    CylinderGeometry,
    PlaneGeometry,
    SphereGeometry
} from 'three';

import {
    type BasePointOfInterest,
    type CornerSingleSidePOI,
    CornerSingleSidePOIPoiType,
    type Line,
    PlacementSolution,
    type PlanarFacePOI,
    PlanarFacePOIPoiType,
    type Point3D,
    SettingModelSettingType,
    type StraightWallPOI,
    StraightWallPOIPoiType
} from 'api/models';
import { removeCachedInstanceEdges } from 'app/modules/wallforming/components/scenes/objects/articles/utils/edgesOverlayUtils';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiGeometryType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiMeshType';
import { getWorldUnitsPerPixel } from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type { ColliderPoint } from 'app/modules/wallforming/components/scenes/objects/helpers/types/colliderPoint';
import type { WallEdge } from 'app/modules/wallforming/components/scenes/objects/helpers/types/wallEdge';
import type { Poi } from 'app/modules/wallforming/components/scenes/objects/pois/types/poi';
import type { PoiMeshData } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiMeshData';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

const CORNER_POI_POSITION_TOLERANCE = 0.1;

/* POI type check */

export function isPlanarFacePoi(toCheck: BasePointOfInterest): toCheck is PlanarFacePOI {
    return (toCheck as PlanarFacePOI).poiType === PlanarFacePOIPoiType.PlanarFacePOI;
}

export function isCornerSingleSidePoi(toCheck: BasePointOfInterest): toCheck is CornerSingleSidePOI {
    return (toCheck as CornerSingleSidePOI).poiType === CornerSingleSidePOIPoiType.CornerSingleSidePOI;
}

export function isStraightWallPoi(toCheck: BasePointOfInterest): toCheck is StraightWallPOI {
    return (toCheck as StraightWallPOI).poiType === StraightWallPOIPoiType.StraightWallPOI;
}

export function getPoiIndexForArticleInstance(
    pois: Poi[],
    articleId: string,
    instanceId: string,
    poiGuids?: string[]
): number {
    for (const poiGuid of poiGuids ?? []) {
        const poiIndex = pois.findIndex((poiData) => poiData.poi.poiGuid === poiGuid);
        if (poiIndex >= 0) {
            return poiIndex;
        }
    }

    return pois.findIndex((poiData) => poiData.articles[articleId]?.includes(instanceId) ?? false);
}

const wallPoiColor = new Color('#bfd7f0');
const cornerPoiColor = new Color(PRIMARY_COLOR_ACCENT_HEX);
const wallPoiHoverColor = new Color('#649fda');
const cornerPoiHoverColor = new Color('#649fda');

export function createPois(pointsOfInterest: BasePointOfInterest[]) {
    const pois: Poi[] = [];
    const colliderPoints: ColliderPoint[] = [];
    const wallEdges: WallEdge[] = [];

    pointsOfInterest.forEach((poi, index) => {
        if (!poi) {
            return;
        }

        const isWallPoi = isPlanarFacePoi(poi);
        const isCornerPoi = isCornerSingleSidePoi(poi);

        pois.push({
            poi,
            filterType: isWallPoi ? POIFilterType.Wall : isCornerPoi ? POIFilterType.Corner : POIFilterType.Corner,
            defaultColor: isWallPoi ? wallPoiColor : isCornerPoi ? cornerPoiColor : wallPoiColor,
            hoverColor: isWallPoi ? wallPoiHoverColor : isCornerPoi ? cornerPoiHoverColor : wallPoiHoverColor,
            autoFormworkPlaced: false,
            articles: {}
        });

        if (!isWallPoi) {
            return;
        }

        const points = poi.formFace.outerWire.edges.map((edge, pointIndex) => ({
            id: `${poi.poiGuid}-${pointIndex}`,
            ownerType: 'building',
            relationId: poi.poiGuid,
            poiIndex: index,
            poiGuids: [poi.poiGuid],
            position: new Vector3(edge.startPoint.x, edge.startPoint.y, edge.startPoint.z),
            name: poi.poiGuid
        }));

        colliderPoints.push(...points);
        for (const edge of poi.formFace.outerWire.edges) {
            wallEdges.push({
                poiGuid: poi.poiGuid,
                start: new Vector3(edge.startPoint.x, edge.startPoint.y, edge.startPoint.z),
                end: new Vector3(edge.endPoint.x, edge.endPoint.y, edge.endPoint.z)
            });
        }
    });

    return { pois, colliderPoints, wallEdges };
}

/** Returns the selected POI and, for a selected corner, the wall faces it owns. */
export function getRelatedPoiIndices(pois: Poi[], poiIndex: number): number[] {
    const poi = pois[poiIndex]?.poi;
    if (!poi) {
        return [];
    }

    if (!isCornerSingleSidePoi(poi)) {
        return [poiIndex];
    }

    const faceGuids = new Set([poi.faceA.poiGuid, poi.faceB.poiGuid]);
    const relatedGuids = new Set([poi.poiGuid, ...faceGuids]);
    pois.forEach((candidate) => {
        const candidatePoi = candidate.poi;
        if (
            isCornerSingleSidePoi(candidatePoi) &&
            (faceGuids.has(candidatePoi.faceA.poiGuid) || faceGuids.has(candidatePoi.faceB.poiGuid)) &&
            Math.abs(candidatePoi.bottomPoint.x - poi.bottomPoint.x) <= CORNER_POI_POSITION_TOLERANCE &&
            Math.abs(candidatePoi.bottomPoint.y - poi.bottomPoint.y) <= CORNER_POI_POSITION_TOLERANCE
        ) {
            relatedGuids.add(candidatePoi.poiGuid);
            relatedGuids.add(candidatePoi.faceA.poiGuid);
            relatedGuids.add(candidatePoi.faceB.poiGuid);
        }
    });

    return pois.flatMap((candidate, index) => (relatedGuids.has(candidate.poi.poiGuid) ? [index] : []));
}

/** Connector scope: a wall POI with its adjacent corners, or a corner POI with its two faces. */
export function getConnectorRelatedPoiIndices(pois: Poi[], poiIndex: number): number[] {
    const poi = pois[poiIndex]?.poi;
    if (!poi) {
        return [];
    }

    const relatedPoiIndices = new Set<number>(
        isCornerSingleSidePoi(poi) ? getRelatedPoiIndices(pois, poiIndex) : [poiIndex]
    );
    if (!isCornerSingleSidePoi(poi)) {
        pois.forEach(({ poi: candidate }, candidateIndex) => {
            if (
                isCornerSingleSidePoi(candidate) &&
                (candidate.faceA.poiGuid === poi.poiGuid || candidate.faceB.poiGuid === poi.poiGuid)
            ) {
                getRelatedPoiIndices(pois, candidateIndex).forEach((relatedIndex) =>
                    relatedPoiIndices.add(relatedIndex)
                );
            }
        });
    }

    return pois.flatMap((_, index) => (relatedPoiIndices.has(index) ? [index] : []));
}

/**
 * Resolves the POI a connector request is made for: the selected POI when its connector scope
 * covers the clamp's wall, otherwise the clamp's own wall POI (corner POI only as last resort).
 */
export function getConnectorRequestPoiIndex(
    pois: Poi[],
    selectedPoiIndex: number | undefined,
    connectorPoiGuids: readonly string[]
): number | undefined {
    const ownWallIndex = pois.findIndex(({ poi }) => isPlanarFacePoi(poi) && connectorPoiGuids.includes(poi.poiGuid));
    const ownIndex =
        ownWallIndex >= 0 ? ownWallIndex : pois.findIndex(({ poi }) => connectorPoiGuids.includes(poi.poiGuid));

    switch (true) {
        case ownIndex < 0:
            return selectedPoiIndex;
        case selectedPoiIndex !== undefined && getConnectorRelatedPoiIndices(pois, selectedPoiIndex).includes(ownIndex):
            return selectedPoiIndex;
        default:
            return ownIndex;
    }
}

/* POI matrix */

export const FACE_COLOR = new Color('#bfd7f0');
export const HOVER_COLOR = new Color('#649fda');
export const SELECTION_COLOR = new Color('#649fda');
export const EDGE_COLOR = new Color(PRIMARY_COLOR_ACCENT_HEX);

const dummy = new Object3D();
const top = new Vector3();
const bottom = new Vector3();
const quaternion = new Quaternion();
const unitVector = new Vector3(0, 1, 0);

export function createCornerMatrix(topPoint: Point3D, bottomPoint: Point3D): Matrix4 {
    top.set(topPoint.x, topPoint.y, topPoint.z);
    bottom.set(bottomPoint.x, bottomPoint.y, bottomPoint.z);
    const center = top.clone().add(bottom).multiplyScalar(0.5);
    const height = top.distanceTo(bottom);
    const direction = top.clone().sub(bottom).normalize();

    dummy.position.copy(center);
    dummy.quaternion.copy(quaternion.setFromUnitVectors(unitVector, direction));
    dummy.scale.set(1, height, 1);
    dummy.updateMatrix();
    return dummy.matrix.clone();
}

const normalVector = new Vector3();
const widthVector = new Vector3();
const heightVector = new Vector3();
const scaleMatrix = new Matrix4();

export function createWallFaceMatrix(edges: Line[], normal: Point3D): Matrix4 {
    if (edges.length === 0) {
        return new Matrix4();
    }

    const points = edges.flatMap((edge) => [edge.startPoint, edge.endPoint]);
    const center = points
        .reduce((accumulator: Vector3, point) => accumulator.add(new Vector3(point.x, point.y, point.z)), new Vector3())
        .multiplyScalar(1 / points.length);

    const firstEdge = edges[0];
    widthVector.set(
        firstEdge.endPoint.x - firstEdge.startPoint.x,
        firstEdge.endPoint.y - firstEdge.startPoint.y,
        firstEdge.endPoint.z - firstEdge.startPoint.z
    );

    const width = Math.max(widthVector.length(), 0.001);
    widthVector.normalize();
    normalVector.set(normal.x, normal.y, normal.z).normalize();
    heightVector.crossVectors(normalVector, widthVector).normalize();

    const height = points.reduce(
        (accumulator, point) => {
            const value = new Vector3(point.x, point.y, point.z).dot(heightVector);
            return {
                min: Math.min(accumulator.min, value),
                max: Math.max(accumulator.max, value)
            };
        },
        { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY }
    );

    const heightSize = Math.max(height.max - height.min, 0.001);
    const matrix = new Matrix4();
    matrix.makeBasis(widthVector, heightVector, normalVector);
    matrix.setPosition(center);
    return matrix.multiply(scaleMatrix.makeScale(width, heightSize, 1));
}

const POINT_SCALE_FACTOR = 20;

export const applyPoiPointTransform = (
    object: Object3D,
    poiData: PoiMeshData,
    camera: Camera,
    viewportHeight: number
): void => {
    object.position.set(poiData.x, poiData.y, poiData.z);

    const worldUnitsPerPixel = getWorldUnitsPerPixel(camera, object.position, viewportHeight);
    object.scale.setScalar(POINT_SCALE_FACTOR * worldUnitsPerPixel * poiData.scale);
};

export const updatePoiBatchedMatrices = <TEntry>(
    mesh: BatchedMesh,
    entries: TEntry[],
    instanceIdByEntryIndex: readonly (number | undefined)[],
    applyMatrix: (entry: TEntry, matrix: Matrix4) => void,
    scratchMatrix: Matrix4
): void => {
    for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
        const instanceId = instanceIdByEntryIndex[entryIndex];
        if (instanceId === undefined) {
            continue;
        }

        applyMatrix(entries[entryIndex], scratchMatrix);
        mesh.setMatrixAt(instanceId, scratchMatrix);
    }
    mesh.boundingBox = null;
    mesh.boundingSphere = null;
};

/* POI geometry */

const sphereGeometry = new SphereGeometry(1, 16, 16);
const planeGeometry = new PlaneGeometry(1, 1);
const cylinderGeometry = new CylinderGeometry(0.03, 0.03, 1, 8, 1);

export const createPoiGeometryByType = (type: PoiGeometryType): Map<POIFilterType, BufferGeometry> => {
    const map = new Map<POIFilterType, BufferGeometry>();
    map.set(POIFilterType.Wall, type === PoiGeometryType.POINTS ? sphereGeometry : planeGeometry);
    map.set(POIFilterType.Corner, type === PoiGeometryType.POINTS ? sphereGeometry : cylinderGeometry);
    return map;
};

/* POI placement visibility */

export function isCornerPlacementPreviewActive(state: PlacementState): boolean {
    return (
        state.showPlacementPreview &&
        state.selectedSettingView === SettingView.AUTOMATED_PLACEMENT &&
        state.selectedSettingType === SettingModelSettingType.Corners &&
        state.automatedPlacementTab === PlacementTab.SOLUTIONS &&
        state.poiSelection.current.filterType === POIFilterType.Corner
    );
}

export function applyCurrentPoiPlacementVisibility(state: PlacementState): void {
    const selectedPoiIndex = state.poiSelection.current.index;
    if (selectedPoiIndex === undefined) {
        return;
    }

    const currentPoiInstanceIds = Object.values(state.pois[selectedPoiIndex]?.articles ?? {}).flat();
    if (currentPoiInstanceIds.length === 0) {
        return;
    }

    const shouldShowCurrentPlacement = !isCornerPlacementPreviewActive(state);
    if (!shouldShowCurrentPlacement) {
        removeCachedInstanceEdges(currentPoiInstanceIds);
    }

    state.updateInstanceVisibility(currentPoiInstanceIds, shouldShowCurrentPlacement);
}

export function applyPrevPoiPlacementVisibility(prevPoiIndex: number | undefined, state: PlacementState): void {
    if (prevPoiIndex === undefined) {
        return;
    }

    const prevPoiInstanceIds = Object.values(state.pois[prevPoiIndex]?.articles ?? {}).flat();
    if (prevPoiInstanceIds.length > 0) {
        state.updateInstanceVisibility(prevPoiInstanceIds, true);
    }
}

/* POI navigation */

const edgeDirection = new Vector3();
const normalA = new Vector3();
const normalB = new Vector3();
const outsideDirection = new Vector3();
const direction = new Vector3();
const directionA = new Vector3();

const getNeighborPoiGuid = (cornerPoi: CornerSingleSidePOI, isShiftPressed: boolean): string => {
    edgeDirection
        .set(
            cornerPoi.topPoint.x - cornerPoi.bottomPoint.x,
            cornerPoi.topPoint.y - cornerPoi.bottomPoint.y,
            cornerPoi.topPoint.z - cornerPoi.bottomPoint.z
        )
        .normalize();

    const faceA = cornerPoi.faceA.formFace;
    const faceB = cornerPoi.faceB.formFace;
    normalA.set(faceA.normal.x, faceA.normal.y, faceA.normal.z).normalize();
    normalB.set(faceB.normal.x, faceB.normal.y, faceB.normal.z).normalize();

    outsideDirection.copy(normalA).add(normalB).projectOnPlane(edgeDirection);
    if (outsideDirection.lengthSq() === 0) {
        return cornerPoi.faceA.poiGuid;
    }
    outsideDirection.normalize();

    if (!isShiftPressed) {
        direction.copy(outsideDirection).cross(edgeDirection).normalize();
    } else {
        direction.copy(edgeDirection).cross(outsideDirection).normalize();
    }
    directionA
        .set(
            cornerPoi.faceA.navigationPoint.x - cornerPoi.bottomPoint.x,
            cornerPoi.faceA.navigationPoint.y - cornerPoi.bottomPoint.y,
            cornerPoi.faceA.navigationPoint.z - cornerPoi.bottomPoint.z
        )
        .projectOnPlane(edgeDirection)
        .normalize();

    return directionA.dot(direction) >= 0 ? cornerPoi.faceA.poiGuid : cornerPoi.faceB.poiGuid;
};

export function getNeighboringPoiIndex(
    pois: Poi[],
    selectedPoiIndex: number | undefined,
    poiType: POIFilterType,
    excludedPoiIndex: number | undefined,
    isShiftPressed: boolean
): number {
    const selectedPoi = selectedPoiIndex === undefined ? undefined : pois[selectedPoiIndex]?.poi;

    if (selectedPoi && poiType === POIFilterType.Wall && isCornerSingleSidePoi(selectedPoi)) {
        const neighboringWallGuid = getNeighborPoiGuid(selectedPoi, isShiftPressed);
        const neighboringWallIndex = pois.findIndex(
            (poi) => isPlanarFacePoi(poi.poi) && poi.poi.poiGuid === neighboringWallGuid
        );
        if (neighboringWallIndex >= 0) {
            return neighboringWallIndex;
        }
    }

    if (selectedPoi && poiType === POIFilterType.Corner && isPlanarFacePoi(selectedPoi)) {
        const directionalCornerIndex = pois.findIndex(
            (poi) =>
                isCornerSingleSidePoi(poi.poi) &&
                (poi.poi.faceA.poiGuid === selectedPoi.poiGuid || poi.poi.faceB.poiGuid === selectedPoi.poiGuid) &&
                getNeighborPoiGuid(poi.poi, !isShiftPressed) === selectedPoi.poiGuid
        );
        if (directionalCornerIndex >= 0) {
            return directionalCornerIndex;
        }

        const neighboringCornerIndex = pois.findIndex(
            (poi, index) =>
                index !== excludedPoiIndex &&
                isCornerSingleSidePoi(poi.poi) &&
                (poi.poi.faceA.poiGuid === selectedPoi.poiGuid || poi.poi.faceB.poiGuid === selectedPoi.poiGuid)
        );
        if (neighboringCornerIndex >= 0) {
            return neighboringCornerIndex;
        }

        const directionalFallbackIndex = pois.findIndex(
            (poi) =>
                isCornerSingleSidePoi(poi.poi) && getNeighborPoiGuid(poi.poi, !isShiftPressed) === selectedPoi.poiGuid
        );
        if (directionalFallbackIndex >= 0) {
            return directionalFallbackIndex;
        }
    }

    return pois.findIndex((poi) => poi.filterType === poiType);
}

/* POI solution placement */

const FRACTION_DIGITS = 3;

function toFixed(value: number) {
    return value.toFixed(FRACTION_DIGITS);
}

export function getSolutionKey(solution: PlacementSolution) {
    return solution.placements
        .flatMap(({ articleGuid, instances }) =>
            instances.flatMap(({ parts }) =>
                parts.map(
                    ({ name, position, rotation }) =>
                        `${articleGuid}|${name}|${toFixed(position.x)},${toFixed(position.y)},${toFixed(position.z)}|${toFixed(rotation)}`
                )
            )
        )
        .sort()
        .join(';');
}
